import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Volume2, VolumeX, Loader2, Wifi, WifiOff } from 'lucide-react';
import useSpeechRecognition from '@/hooks/useSpeechRecognition';
import useTTS from '@/hooks/useTTS';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VoiceChat Component - Enables voice-based interaction with tutors
 */
const VoiceChat = ({
  tutor = 'toby',
  module = 'general',
  context = {},
  sessionId,
  onResponse = () => {},
  onListeningChange = () => {},
  onSpeakingChange = () => {},
  disabled = false,
  className = ''
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastTranscript, setLastTranscript] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const processingRef = useRef(false);
  const ttsRef = useRef(null);

  // TTS hook - defined first so it can be used in callbacks
  const tts = useTTS({
    tutor: tutor,
    voiceEnabled: voiceEnabled,
    onSpeechStart: () => {
      onSpeakingChange(true);
    },
    onSpeechEnd: () => {
      onSpeakingChange(false);
    }
  });

  // Store tts in ref for use in callbacks
  useEffect(() => {
    ttsRef.current = tts;
  }, [tts]);

  // Play audio from URL (ElevenLabs response)
  const playAudio = useCallback((audioUrl) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);
      
      audio.onplay = () => {
        onSpeakingChange(true);
      };
      
      audio.onended = () => {
        onSpeakingChange(false);
        resolve();
      };
      
      audio.onerror = (err) => {
        onSpeakingChange(false);
        reject(err);
      };
      
      audio.play().catch(reject);
    });
  }, [onSpeakingChange]);

  // Handle transcript from speech recognition
  const handleTranscript = useCallback(async (transcript) => {
    if (!transcript.trim() || processingRef.current) return;
    
    processingRef.current = true;
    setIsProcessing(true);
    setLastTranscript(transcript);

    try {
      // Send to voice chat API
      const response = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transcript: transcript,
          session_id: sessionId,
          tutor: tutor,
          module: module,
          context: context,
          include_audio: voiceEnabled
        })
      });

      const data = await response.json();
      
      // Notify parent of response
      onResponse(data);

      // Play audio if available (check for mock flag)
      if (data.audio_url && !data.mock && voiceEnabled) {
        await playAudio(data.audio_url);
      } else if (voiceEnabled && data.response) {
        // Use Web Speech fallback directly
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(data.response);
          utterance.lang = 'hi-IN';
          utterance.rate = tutor === 'spark' ? 1.1 : 0.95;
          utterance.pitch = tutor === 'spark' ? 1.2 : 0.9;
          
          utterance.onstart = () => onSpeakingChange(true);
          utterance.onend = () => onSpeakingChange(false);
          utterance.onerror = () => onSpeakingChange(false);
          
          window.speechSynthesis.speak(utterance);
        }
      }

    } catch (error) {
      console.error('Voice chat error:', error);
    } finally {
      setIsProcessing(false);
      processingRef.current = false;
    }
  }, [sessionId, tutor, module, context, voiceEnabled, onResponse, playAudio, onSpeakingChange]);

  // Speech recognition hook
  const {
    isListening,
    interimTranscript,
    error: sttError,
    useDeepgram,
    startListening,
    stopListening,
  } = useSpeechRecognition({
    onTranscript: handleTranscript,
    continuous: true,
    language: 'hi',
    autoRestart: true
  });

  // Toggle voice chat active state
  const toggleVoiceChat = useCallback(() => {
    if (isActive) {
      stopListening();
      tts.stopSpeaking();
      setIsActive(false);
      onListeningChange(false);
    } else {
      startListening();
      setIsActive(true);
      onListeningChange(true);
    }
  }, [isActive, startListening, stopListening, tts, onListeningChange]);

  // Update parent when listening state changes
  useEffect(() => {
    onListeningChange(isListening);
  }, [isListening, onListeningChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      if (ttsRef.current) {
        ttsRef.current.stopSpeaking();
      }
    };
  }, [stopListening]);

  return (
    <div className={`voice-chat-container ${className}`}>
      {/* Voice Status Indicator */}
      <div className="flex items-center gap-3 mb-3">
        {/* Connection Status */}
        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
          useDeepgram ? 'bg-seagreen/10 text-seagreen' : 'bg-orange-100 text-orange-600'
        }`}>
          {useDeepgram ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {useDeepgram ? 'Deepgram' : 'Web Speech'}
        </div>

        {/* Voice Output Status */}
        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
          tts.useElevenLabs ? 'bg-spark/10 text-spark' : 'bg-orange-100 text-orange-600'
        }`}>
          <Volume2 className="w-3 h-3" />
          {tts.useElevenLabs ? 'ElevenLabs' : 'Browser Voice'}
        </div>
      </div>

      {/* Main Voice Control */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="voice-toggle-btn"
          onClick={toggleVoiceChat}
          disabled={disabled || isProcessing}
          className={`rounded-full w-16 h-16 transition-all ${
            isActive 
              ? isListening
                ? 'bg-seagreen hover:bg-seagreen-dark animate-pulse'
                : 'bg-spark hover:bg-spark/80'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {isProcessing ? (
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          ) : isActive ? (
            <Mic className="w-8 h-8 text-white" />
          ) : (
            <MicOff className="w-8 h-8 text-muted-foreground" />
          )}
        </Button>

        {/* Status Text */}
        <div className="flex-1">
          <p className="font-medium text-sm text-foreground">
            {isProcessing ? 'Processing...' : isListening ? 'Listening... Speak now!' : isActive ? 'Speaking...' : 'Click to start voice chat'}
          </p>
          
          {/* Interim Transcript */}
          <AnimatePresence>
            {interimTranscript && (
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs text-muted-foreground italic mt-1"
              >
                "{interimTranscript}"
              </motion.p>
            )}
          </AnimatePresence>

          {/* Last Transcript */}
          {lastTranscript && !interimTranscript && (
            <p className="text-xs text-seagreen mt-1">
              You said: "{lastTranscript}"
            </p>
          )}
        </div>

        {/* Voice Output Toggle */}
        <Button
          data-testid="voice-output-toggle"
          variant="ghost"
          size="sm"
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          className="rounded-full"
        >
          {voiceEnabled ? (
            <Volume2 className="w-5 h-5 text-seagreen" />
          ) : (
            <VolumeX className="w-5 h-5 text-muted-foreground" />
          )}
        </Button>
      </div>

      {/* Error Display */}
      {sttError && (
        <p className="text-xs text-destructive mt-2">
          Speech error: {sttError}
        </p>
      )}

      {/* Visual Waveform Animation */}
      {isListening && (
        <div className="flex justify-center gap-1 mt-4">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="w-1 bg-seagreen rounded-full"
              animate={{
                height: [8, 24, 8],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                delay: i * 0.1,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceChat;
